import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchRequestCount } from "slices/countSlice";
import { disconnectSocket } from "services/websocket";
import useLogoutSession from "shared/hooks/useSessionLogout";
import { getSideBarList } from "./sidebarConfig";
import { routesPermissions } from "app/routes/routes";
import {
  SHOW_PORTAL_SELECTION_MODAL,
  userCanAccessDeskPortal,
} from "feature/authv2/portalDeskConfig";

/** Current dashboard page from URL (e.g. /dashboard/driver_list → "driver_list") */
function getTableFromPathname(pathname) {
  const match = pathname.match(/^\/dashboard\/([^/]+)/);
  return match ? match[1] : undefined;
}

export function useSidebar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  /** First path segment after /dashboard/ — used for sidebar section expand and which item matches. */
  const table = useMemo(() => getTableFromPathname(location.pathname), [location.pathname]);
  const reduxState = useSelector((state) => state);
  const recordCounts = useSelector((state) => state?.requestCount?.requestCount) || {};
  const allFleetRecordCount = useSelector((state) => state?.allFleetHubAPIDetails?.recordCounts);

  const role = reduxState.role?.roles?.[0] ?? null;
  const userRoles = useMemo(() => reduxState.role?.roles ?? [], [reduxState.role]);
  const showDeskPortalMenu = useMemo(
    () => SHOW_PORTAL_SELECTION_MODAL && userCanAccessDeskPortal(userRoles),
    [userRoles]
  );
  const userName = reduxState.role?.name ?? "";
  const userEmail = reduxState.role?.data?.email ?? "";

  const [activeOptionKey, setActiveOptionKey] = useState(null);

  const sideBarList = useMemo(
    () => getSideBarList(recordCounts, allFleetRecordCount, role, routesPermissions),
    [recordCounts, allFleetRecordCount, role, routesPermissions]
  );

  useEffect(() => {
    dispatch(fetchRequestCount());
  }, [dispatch]);

  /** Returns section optionsKey (e.g. "driverList") for a path so CollapseBar activeKey matches item keys. */
  const getDefaultActiveKey = useCallback(
    (path) => {
      if (!path || !sideBarList?.length) return null;
      for (const item of sideBarList) {
        const hasPath = item?.options?.some(
          (opt) => opt?.sidebarListClick === path || opt?.sidebarBasePath === path
        );
        if (hasPath && item.optionsKey) return item.optionsKey;
      }
      return null;
    },
    [sideBarList]
  );

  // Keep expanded section and highlight in sync with current URL
  useEffect(() => {
    const sectionKey = getDefaultActiveKey(table);
    setActiveOptionKey(sectionKey);
  }, [table, getDefaultActiveKey]);

  const setTableParams = (value) => navigate(`/dashboard/${value}`);

  const maindashboardClick = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const navigateToDeskPortal = useCallback(() => {
    if (showDeskPortalMenu) {
      navigate("/desk");
    }
  }, [navigate, showDeskPortalMenu]);

  const handleLogoutFromSession = useLogoutSession();
  const handleLogout = () => {
    disconnectSocket();
    handleLogoutFromSession();
  };

  return {
    role,
    userName,
    userEmail,
    table,
    pathname: location.pathname,
    recordCounts,
    sideBarList,
    activeOptionKey,
    setActiveOptionKey,
    getDefaultActiveKey,
    setTableParams,
    maindashboardClick,
    navigateToDeskPortal,
    showDeskPortalMenu,
    handleLogout,
    dispatch,
  };
}
